import React from 'react';
import { Avatar, Box, Divider, IconButton, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography, useMediaQuery, } from '@mui/material';
import { DarkMode as DarkModeIcon, LightMode as LightModeIcon, Logout as LogoutIcon, MoreVert as MoreVertIcon, OpenInNew as OpenInNewIcon, Person as PersonIcon, } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { useAuth } from '@wheeebo/polaris-api';
import { ColorModeContext } from '../colorMode';
const msgs = defineMessages({
    buttonGoToAdminPage: {
        id: 'nav.PageHeaderMenuBar.button.goToAdminPage',
        defaultMessage: 'Admin Website',
    },
    buttonGoToAtcPage: {
        id: 'nav.PageHeaderMenuBar.button.goToAtcPage',
        defaultMessage: 'ATC Website',
    },
    buttonGoToMemberPage: {
        id: 'nav.PageHeaderMenuBar.button.goToMemberPage',
        defaultMessage: 'Member Website',
    },
    buttonLogOut: {
        id: 'nav.PageHeaderMenuBar.button.logOut',
        defaultMessage: 'Log out',
    },
    buttonUploadGame: {
        id: 'nav.PageHeaderMenuBar.button.uploadGame',
        defaultMessage: 'Upload Game',
    },
    buttonSignIn: {
        id: 'nav.PageHeaderMenuBar.button.signIn',
        defaultMessage: 'Sign In',
    },
});
const ProfileMenu = React.memo(({ onClose, anchorElement, adminWebsiteURL, atcWebsiteURL, memberWebsiteURL }) => {
    const intl = useIntl();
    const isMenuOpen = Boolean(anchorElement);
    const { account, signOut } = useAuth();
    const navigate = useNavigate();
    const handleLogout = async () => {
        onClose();
        signOut?.();
    };
    const handleProfileClick = () => {
        onClose();
        navigate('/settings');
    };
    return (React.createElement(Menu, { anchorEl: anchorElement, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, keepMounted: true, transformOrigin: { vertical: 'top', horizontal: 'center' }, open: isMenuOpen, onClose: onClose },
        React.createElement(MenuItem, { onClick: handleProfileClick, sx: { justifyContent: 'center', flexDirection: 'column', p: 3, minWidth: 210 } },
            React.createElement(Avatar, { sx: { width: 52, height: 52, m: 2 }, src: account?.avatarUrl },
                React.createElement(PersonIcon, { color: "disabled" })),
            React.createElement(Typography, { variant: 'body1', sx: { whiteSpace: 'pre-wrap', wordWrap: 'break-word', overflowWrap: 'anywhere' } }, account?.name || '')),
        React.createElement(Divider, null),
        adminWebsiteURL ? (React.createElement(ListItemButton, { dense: true, href: adminWebsiteURL, target: "_blank", sx: { justifyContent: 'space-between' } },
            React.createElement(ListItemText, { primary: intl.formatMessage(msgs.buttonGoToAdminPage) }),
            React.createElement(ListItemIcon, { sx: { minWidth: 'auto' } }, React.createElement(OpenInNewIcon, { fontSize: "small" })))) : null,
        atcWebsiteURL ? (React.createElement(ListItemButton, { dense: true, href: atcWebsiteURL, target: "_blank", sx: { justifyContent: 'space-between' } },
            React.createElement(ListItemText, { primary: intl.formatMessage(msgs.buttonGoToAtcPage) }),
            React.createElement(ListItemIcon, { sx: { minWidth: 'auto' } }, React.createElement(OpenInNewIcon, { fontSize: "small" })))) : null,
        memberWebsiteURL ? (React.createElement(ListItemButton, { dense: true, href: memberWebsiteURL, target: "_blank", sx: { justifyContent: 'space-between' } },
            React.createElement(ListItemText, { primary: intl.formatMessage(msgs.buttonGoToMemberPage) }),
            React.createElement(ListItemIcon, { sx: { minWidth: 'auto' } }, React.createElement(OpenInNewIcon, { fontSize: "small" })))) : null,
        React.createElement(Divider, null),
        React.createElement(ListItemButton, { dense: true, onClick: handleLogout, sx: { justifyContent: 'space-between' } },
            React.createElement(ListItemText, { primary: intl.formatMessage(msgs.buttonLogOut) }),
            React.createElement(ListItemIcon, { sx: { minWidth: 'auto' } },
                React.createElement(LogoutIcon, { fontSize: "small" })))));
});
function PageHeaderMenuBar({ adminWebsiteURL, atcWebsiteURL, memberWebsiteURL }) {
    const colorMode = React.useContext(ColorModeContext);
    const theme = useTheme();
    const [profileMenuElem, setProfileMenuElem] = React.useState(null);
    const handleProfileMenuOpen = (event) => setProfileMenuElem(event.currentTarget);
    const handleMenuClose = () => setProfileMenuElem(null);
    const { account } = useAuth();
    const isBigScreen = useMediaQuery(theme.breakpoints.up('sm'));
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { direction: "row", gap: 1, sx: {
                display: 'none',
                alignItems: 'center',
                [theme.breakpoints.up('md')]: {
                    display: 'flex',
                },
            } },
            isBigScreen ? (React.createElement(IconButton, { onClick: colorMode.toggleTheme, color: "inherit" }, colorMode.isThemeDark ? React.createElement(LightModeIcon, null) : React.createElement(DarkModeIcon, null))) : null,
            React.createElement(IconButton, { edge: "end", "aria-label": "account of current user", "aria-haspopup": "true", onClick: handleProfileMenuOpen, color: "inherit" },
                React.createElement(Avatar, { sx: { width: 32, height: 32 }, src: account?.avatarUrl },
                    React.createElement(PersonIcon, { color: "disabled" })))),
        React.createElement(Box, { sx: {
                display: 'flex',
                [theme.breakpoints.up('md')]: {
                    display: 'none',
                },
            } },
            React.createElement(IconButton, { sx: { p: 0, pr: 1 / 2, ml: -1 / 2 }, edge: "end", "aria-label": "show more", "aria-haspopup": "true", onClick: handleProfileMenuOpen, color: "inherit" },
                React.createElement(MoreVertIcon, null))),
        React.createElement(ProfileMenu, { onClose: handleMenuClose, anchorElement: profileMenuElem, adminWebsiteURL: adminWebsiteURL, atcWebsiteURL: atcWebsiteURL, memberWebsiteURL: memberWebsiteURL })));
}
export default React.memo(PageHeaderMenuBar);
